/**
 * PaymentSuccess Page
 * Displays payment success confirmation after Stripe checkout
 */

import React, { useState, useEffect } from 'react';
import { apiUrl } from '../../config';
import './PaymentSuccess.css';

export const PaymentSuccess = () => {
  const [verifying, setVerifying] = useState(true);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState(null);
  const [sessionInfo, setSessionInfo] = useState(null);

  useEffect(() => {
    const verifySession = async () => {
      // Get session_id from URL query params
      const urlParams = new URLSearchParams(window.location.search);
      const sessionId = urlParams.get('session_id');

      if (!sessionId) {
        setError('No session ID found');
        setVerifying(false);
        return;
      }

      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) {
        setError('Please log in to continue');
        setVerifying(false);
        return;
      }

      try {
        const response = await fetch(apiUrl(`/api/billing/session/${sessionId}`), {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        });

        const data = await response.json();

        if (data.success && data.session) {
          setSessionInfo(data.session);
          setVerified(true);
        } else {
          setError(data.error || 'Unable to verify payment');
        }
      } catch (err) {
        console.error('Session verification error:', err);
        setError('Unable to verify payment. Please contact support.');
      } finally {
        setVerifying(false);
      }
    };

    verifySession();
  }, []);

  if (verifying) {
    return (
      <div className="payment-status-page">
        <h1>Verifying Payment...</h1>
        <p>Please wait while we confirm your subscription.</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="payment-status-page">
        <h1>Verification Error</h1>
        <p>{error}</p>
        <a href="/login" className="payment-status-link">Back to app</a>
      </div>
    );
  }

  if (verified && sessionInfo) {
    return (
      <div className="payment-status-page">
        <h1>Payment Successful!</h1>
        <p>Your {sessionInfo.plan_name} subscription is now active.</p>
        {sessionInfo.subscription_status === 'trialing' && (
          <p>Your 14-day free trial has started. You won't be charged until the trial ends.</p>
        )}
        <a href="/login" className="payment-status-link">Start using AlgoDentra</a>
      </div>
    );
  }

  return (
    <div className="payment-status-page">
      <h1>Payment Successful</h1>
      <p>Your subscription is being activated. You can close this tab.</p>
      <a href="/login" className="payment-status-link">Back to app</a>
    </div>
  );
};
